// light-sources.ts — ENCOUNTER LIGHT SOURCES & STATUS EFFECTS. Byte-faithful port of
// includes/js/light-sources-init.js (XZ closure Phase C, 2026-06-13; originally template.js ~8659-8940).
//
// Public surface (all called from outside): syncEncounterLightSourceAbilityButtons,
// ftRefreshEncounterDataAfterGmLightToggle, ensureEncounterInventoryAbilityLightOn,
// toggleEncounterPanelAbilityLight, + _ftGetEncounterArrForLightSourcePanel (template.js typeof-guards
// then calls it). The other _ft* helpers + getSingleTargetedLightSourceInventoryId stay module-local.
// All deps resolve at call time as window globals (see globals.d.ts). Side effects on load: NONE.

import { inventoryField } from '../shared/array-types';

const w = window;

function lightRecord(value: unknown): Record<string, unknown> | null {
	return value !== null && typeof value === 'object' && !Array.isArray(value)
		? value as Record<string, unknown>
		: null;
}

function _ftAbilityLightIdsFromEncounterPanel(): string[] {
	const panel = document.getElementById('ft-encounter-light-source-panel');
	const raw = panel?.dataset?.abilityLightIds;
	if (!raw) return [];
	try {
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
	} catch (_e) {
		return [];
	}
}

function _ftGetEncounterArrForLightSourcePanel() {
	const encounterId = String(w.ftGetActiveEncounterId?.() || '').trim();
	if (!encounterId) return null;
	return window.FateTide?.encounterCache?.get(encounterId) || null;
}

function _ftFindEncounterInventoryRow(encounterArr: unknown, inventoryId: unknown): Record<string, unknown> | null {
	const list = lightRecord(encounterArr)?.encounter_inventories_arr;
	if (!Array.isArray(list)) return null;
	return list.map(lightRecord).find((row) => {
		if (!row) return false;
		return inventoryField(row, 'inventory_id') === inventoryId;
	}) ?? null;
}

function _ftCollectOtherAbilityLightStatusIds(invRow: unknown, exceptAbilityId: unknown, abilityLightIds: string[]): string[] {
	const out: string[] = [];
	if (!invRow || !Array.isArray(abilityLightIds) || abilityLightIds.length === 0) return out;
	const exceptLc = String(exceptAbilityId || '').toLowerCase();
	const statuses = ftCreatureStatuses(invRow, 'target');
	if (!Array.isArray(statuses)) return out;
	for (const eff of statuses) {
		if (!eff || statusEffectInstanceIsCampaignPaused(eff)) continue;
		const itemId = String(ftItemsCol(eff, 'item_id') ?? '');
		const hit = abilityLightIds.some((a) => a.toLowerCase() === itemId.toLowerCase());
		if (!hit || itemId.toLowerCase() === exceptLc) continue;
		const statusRecord = lightRecord(eff);
		const rawStatusId = statusRecord?.status_id;
		const sid = Array.isArray(rawStatusId) ? rawStatusId[0] : rawStatusId;
		if (typeof sid === 'string' && checkIfValidUUID(sid)) out.push(sid);
	}
	return out;
}

/**
 * Exactly one targeted .inventory-item with data-item_type=light_source — used by encounter GM light panel.
 * @returns inventory UUID
 */
function getSingleTargetedLightSourceInventoryId() {
	const els = document.querySelectorAll('.targeted.inventory-item[data-item_type="light_source"]');
	if (els.length === 0) {
		displayWarning('Target exactly one light source on the map (click token to toggle target).');
		return null;
	}
	if (els.length > 1) {
		displayWarning('Only one light source can be targeted for lighting toggles. Clear extra targets.');
		return null;
	}
	const id = (els[0] as HTMLElement).dataset.inventory_id;
	if (!checkIfValidUUID(id)) {
		displayWarning('Invalid light source inventory id.');
		return null;
	}
	return id;
}

// =============================================================================
// ENCOUNTER LIGHT SOURCES & STATUS EFFECTS
// Light-source ability button sync, inventory enchantment helpers,
// status-effect refresh logic for encounter panels.
// =============================================================================
function syncEncounterLightSourceAbilityButtons() {
	const panel = document.getElementById('ft-encounter-light-source-panel');
	if (!panel) return;
	const abilityLightIds = _ftAbilityLightIdsFromEncounterPanel();
	const lightEls = document.querySelectorAll('.targeted.inventory-item[data-item_type="light_source"]');
	let inventoryId = null;
	if (lightEls.length === 1) {
		const cand = (lightEls[0] as HTMLElement).dataset.inventory_id;
		if (checkIfValidUUID(cand)) inventoryId = cand;
	}
	const enc = inventoryId ? _ftGetEncounterArrForLightSourcePanel() : null;
	const invRow = enc && inventoryId ? _ftFindEncounterInventoryRow(enc, inventoryId) : null;
	let activeAbilityId = '';
	if (invRow && Array.isArray(ftCreatureStatuses(invRow, 'target')) && abilityLightIds.length) {
		for (const eff of ftCreatureStatuses(invRow, 'target')) {
			if (!eff || statusEffectInstanceIsCampaignPaused(eff)) continue;
			const itemId = String(ftItemsCol(eff, 'item_id') ?? '');
			const match = abilityLightIds.find((a) => a.toLowerCase() === itemId.toLowerCase());
			if (match) {
				activeAbilityId = match;
				break;
			}
		}
	}
	panel.querySelectorAll<HTMLElement>('[data-ft-encounter-light-ability]').forEach((btn) => {
		const aid = btn.getAttribute('data-ft-encounter-light-ability') || '';
		const on = Boolean(
			aid && activeAbilityId && aid.toLowerCase() === String(activeAbilityId).toLowerCase(),
		);
		btn.classList.toggle('active', on);
		btn.setAttribute('aria-pressed', on ? 'true' : 'false');
	});
}

async function ftRefreshEncounterDataAfterGmLightToggle(encounterId: string) {
	if (!encounterId) return;
	// Post-mutation refresh: a GM light toggle just changed encounter state, so force a fresh pull
	// past the now-cache-first getEncounterArr (bust) — the cache may still hold the pre-toggle blob
	// until the WS frame lands, and this result is fed straight to handleEncounterUpdate to re-render.
	const fresh = await getEncounterArr(encounterId, true);
	if (!fresh || !ftEncounterField(fresh, 'encounter_id')) return;
	if (window.FateTide?.encounterCache instanceof Map) {
		window.FateTide.encounterCache.set(encounterId, fresh);
	}
	if (typeof handleEncounterUpdate === 'function') {
		await handleEncounterUpdate({ encounter_arr: fresh });
	}
}

/**
 * Turn one AbilityLightType ON for an encounter inventory row: remove other AbilityLightType rows first, then apply if missing
 * (same semantics as toggleInventoryAbilityLight OFF branch; avoids duplicate add when server already applied on place).
 */
async function ensureEncounterInventoryAbilityLightOn(
	inventoryId: string,
	abilityId: string,
	pushCampaignId: string,
	pushEncounterId: string,
	skipOpenItemInfo = true,
) {
	const abilityLightIds = _ftAbilityLightIdsFromEncounterPanel();
	if (
		!abilityLightIds.length
		|| !checkIfValidUUID(inventoryId)
		|| !checkIfValidUUID(abilityId)
		|| !checkIfValidUUID(pushEncounterId)
	) {
		return false;
	}
	const sl = getSidelogContext();
	if (!sl) {
		return false;
	}
	let enc =
		pushEncounterId && typeof getEncounterArr === 'function'
			? await getEncounterArr(pushEncounterId)
			: null;
	if (enc && pushEncounterId && window.FateTide?.encounterCache instanceof Map) {
		window.FateTide.encounterCache.set(pushEncounterId, enc);
	}
	let invRow = enc ? _ftFindEncounterInventoryRow(enc, inventoryId) : null;
	if (!invRow && pushEncounterId && typeof getEncounterArr === 'function') {
		await ftRefreshEncounterDataAfterGmLightToggle(pushEncounterId);
		enc = await getEncounterArr(pushEncounterId);
		if (enc && window.FateTide?.encounterCache instanceof Map) {
			window.FateTide.encounterCache.set(pushEncounterId, enc);
		}
		invRow = enc ? _ftFindEncounterInventoryRow(enc, inventoryId) : null;
	}
	if (!invRow) {
		if (pushEncounterId) {
			await ftRefreshEncounterDataAfterGmLightToggle(pushEncounterId);
		}
		syncEncounterLightSourceAbilityButtons();
		return false;
	}
	let isThisOn = false;
	if (invRow && Array.isArray(ftCreatureStatuses(invRow, 'target'))) {
		for (const eff of ftCreatureStatuses(invRow, 'target')) {
			if (!eff || statusEffectInstanceIsCampaignPaused(eff)) continue;
			const itemId = String(ftItemsCol(eff, 'item_id') ?? '');
			if (itemId.toLowerCase() === String(abilityId).toLowerCase()) {
				isThisOn = true;
				break;
			}
		}
	}
	const otherIds = invRow ? _ftCollectOtherAbilityLightStatusIds(invRow, abilityId, abilityLightIds) : [];

	const roll_private = sl.roll_private;
	const camp = pushCampaignId || sl.campaign_id;
	const encResolved = pushEncounterId || sl.encounter_id;
	const warn = 'div_sidelog_warning';

	forceOpenSideLog();
	document.getElementById('pills_combat_log_tab')?.click();

	// N6e (2026-10-02, R-065): the status-truth seams (websocket/status-truth.ts — the item's status through the commit's inventory-holder
	// mode); the legacy delete_creature_status_effect_inc.php and add_status_effect.php are both DELETED (A8: the persistent-AoE add is
	// zone-add.ts + the status commit's zone mode).
	void camp; void warn;
	const seams = window as unknown as { ftTryCommitStatusDelete?: (i: { statusId: string; scope: 'single' }) => Promise<unknown>;
		ftTryCommitStatusAdd?: (i: { targetId: string; statusItemId: string; casterId: string; durationInt: string; durationType: string; rollPrivate?: number; encounterId?: string }) => Promise<unknown> };
	if (typeof seams.ftTryCommitStatusDelete !== 'function' || typeof seams.ftTryCommitStatusAdd !== 'function') {
		displayWarning('Reload the page to light or snuff this.');
		return false;
	}
	for (const sid of otherIds || []) {
		if (!checkIfValidUUID(sid)) {
			continue;
		}
		await seams.ftTryCommitStatusDelete({ statusId: sid, scope: 'single' });
	}

	if (!isThisOn) {
		const added = await seams.ftTryCommitStatusAdd({ targetId: inventoryId, statusItemId: String(abilityId), casterId: '', durationInt: '1', durationType: 'permanent',
			rollPrivate: Number(roll_private) || 0, encounterId: encResolved || undefined });
		if (added === null) displayWarning('That light could not be lit here.');
	}

	if (!skipOpenItemInfo && checkIfValidUUID(inventoryId)) {
		openItemInfo(inventoryId, false);
	}
	if (pushEncounterId) {
		await ftRefreshEncounterDataAfterGmLightToggle(pushEncounterId);
	}
	syncEncounterLightSourceAbilityButtons();
	return true;
}

/**
 * GM encounter inventory panel: toggle AbilityLightType on the single targeted light_source token.
 */
async function toggleEncounterPanelAbilityLight(abilityId: unknown) {
	const inventoryId = getSingleTargetedLightSourceInventoryId();
	if (!inventoryId) return;
	const abilityLightIds = _ftAbilityLightIdsFromEncounterPanel();
	if (!abilityLightIds.length) {
		displayWarning('Lighting panel is not available on this page.');
		return;
	}
	let enc = _ftGetEncounterArrForLightSourcePanel();
	let invRow = enc ? _ftFindEncounterInventoryRow(enc, inventoryId) : null;
	const context = lightRecord(w.ftGetEncounterContext?.()) || getSidelogContext() || {};
	const activeEncounterId = String(context.encounter_id || w.ftGetActiveEncounterId?.() || '').trim();
	if (!invRow && activeEncounterId) {
		enc = await getEncounterArr(activeEncounterId);
		if (enc && window.FateTide?.encounterCache instanceof Map) {
			window.FateTide.encounterCache.set(activeEncounterId, enc);
		}
		invRow = enc ? _ftFindEncounterInventoryRow(enc, inventoryId) : null;
	}
	let isOn = false;
	let statusId = '';
	if (invRow && Array.isArray(ftCreatureStatuses(invRow, 'target'))) {
		for (const eff of ftCreatureStatuses(invRow, 'target')) {
			if (!eff || statusEffectInstanceIsCampaignPaused(eff)) continue;
			const itemId = String(ftItemsCol(eff, 'item_id') ?? '');
			if (itemId.toLowerCase() === String(abilityId).toLowerCase()) {
				isOn = true;
				const statusRecord = lightRecord(eff);
				const rawStatusId = statusRecord?.status_id;
				statusId = String((Array.isArray(rawStatusId) ? rawStatusId[0] : rawStatusId) ?? '');
				break;
			}
		}
	}
	const otherIds = invRow ? _ftCollectOtherAbilityLightStatusIds(invRow, abilityId, abilityLightIds) : [];
	const camp = String(context.campaign_id || w.ftGetCampaignId?.() || '').trim();
	const pushEnc = activeEncounterId;
	await toggleInventoryAbilityLight(
		isOn,
		statusId,
		inventoryId,
		abilityId,
		otherIds,
		camp,
		pushEnc,
		true,
	);
	if (activeEncounterId) {
		await ftRefreshEncounterDataAfterGmLightToggle(activeEncounterId);
	}
	syncEncounterLightSourceAbilityButtons();
}

// =============================================================================
// Cross-file consumer exports (3 template.js sites + 1 PHP onclick handler).
// =============================================================================
Object.assign(w, {
	syncEncounterLightSourceAbilityButtons,
	ftRefreshEncounterDataAfterGmLightToggle,
	ensureEncounterInventoryAbilityLightOn,
	toggleEncounterPanelAbilityLight,
	// Private helper exposed because template.js typeof-guards then calls this:
	_ftGetEncounterArrForLightSourcePanel,
});

export {};
